/**
 * 保险合同相关接口兼容层 + 仅三页使用的 Cookie 注入
 */

/**
 * 为保险三页请求附加 Cookie（不影响其他接口）
 * @param {Object} data 请求参数（需含 token）
 * @param {Object} [extraHeaders]
 * @returns {Object} axios 额外配置
 */
export function insureRequestAuth(data, extraHeaders) {
  var token = (data && data.token) || "";
  if (!token) {
    try {
      token = JSON.parse(window.localStorage.getItem("data") || "{}").data || "";
    } catch (e) {}
  }
  if (token) {
    try {
      document.cookie =
        "rescue_account_token=" +
        encodeURIComponent(token) +
        "; path=/; max-age=" +
        60 * 60 * 6;
    } catch (e) {}
  }
  var headers = Object.assign({}, extraHeaders || {}, {
    Cookie: "rescue_account_token=" + token,
  });
  // 不能开 withCredentials：服务端 CORS 为 * 时会与 credentials 冲突
  return {
    headers: headers,
  };
}

/**
 * 判断是否为业务 JSON 响应
 * @param {*} data
 * @returns {boolean}
 */
export function isBizJson(data) {
  return !!(data && typeof data === "object" && data.code !== undefined && data.code !== null);
}

/**
 * 从 HTML 中提取表格行单元格
 * @param {string} html
 * @returns {string[][]}
 */
export function parseHtmlTableRows(html) {
  if (!html || typeof html !== "string") return [];
  var rows = [];
  var trReg = /<tr[^>]*>([\s\S]*?)<\/tr>/gi;
  var trMatch;
  while ((trMatch = trReg.exec(html))) {
    var trHtml = trMatch[1];
    if (/<thead[\s>]/i.test(trMatch[0]) || /<td[^>]*>\s*序号\s*</i.test(trHtml)) continue;
    if (!/<td/i.test(trHtml)) continue;
    var cells = [];
    var tdReg = /<td[^>]*>([\s\S]*?)<\/td>/gi;
    var tdMatch;
    while ((tdMatch = tdReg.exec(trHtml))) cells.push(tdMatch[1]);
    if (cells.length) rows.push(cells);
  }
  return rows;
}

/**
 * @param {string} html
 * @returns {string}
 */
export function stripHtml(html) {
  return String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * @param {string} html
 * @param {string} fnName
 * @returns {string}
 */
export function extractFnArg(html, fnName) {
  var re = new RegExp(fnName + "\\(\\s*['\"]?([\\w-]+)['\"]?\\s*\\)", "i");
  var m = String(html || "").match(re);
  return m ? m[1] : "";
}

/**
 * @param {string} html
 * @returns {string}
 */
export function extractHref(html) {
  var m = String(html || "").match(/href\s*=\s*['"]([^'"]+)['"]/i);
  return m ? m[1] : "";
}

/**
 * @param {*} data
 * @returns {{code:number, list:Array, msg?:string}}
 */
export function normalizeRegulatorList(data) {
  if (isBizJson(data)) {
    if (Number(data.code) !== 0) {
      return { code: Number(data.code), list: [], msg: data.msg };
    }
    var obj = data.obj || {};
    var ulst = obj.ulst || [];
    var instiMap = obj.instiMap || {};
    return {
      code: 0,
      list: ulst.map(function (user, idx) {
        var insti = instiMap[user.instiId] || {};
        return {
          index: idx + 1,
          userId: user.id,
          instiId: user.instiId,
          passport: user.passport || user.mobileNo || "",
          email: user.email || insti.email || "",
          name: insti.name || "",
          shortName: insti.shortName || "",
          contact: insti.contact || user.name || "",
          contactTel: insti.contactTel || user.mobileNo || "",
        };
      }),
    };
  }
  if (typeof data === "string") {
    return {
      code: 0,
      list: parseHtmlTableRows(data).map(function (cells, idx) {
        return {
          index: Number(stripHtml(cells[0])) || idx + 1,
          name: stripHtml(cells[1]),
          shortName: stripHtml(cells[2]),
          contact: stripHtml(cells[3]),
          contactTel: stripHtml(cells[4]),
          email: stripHtml(cells[5]),
          passport: stripHtml(cells[6]),
          userId: extractFnArg(cells[7] || "", "resetPw"),
          instiId: "",
        };
      }),
    };
  }
  return { code: -1, list: [], msg: "无法解析客户列表响应" };
}

/**
 * @param {*} data
 * @returns {{code:number, list:Array, msg?:string}}
 */
export function normalizeContractList(data) {
  var TYPE_MAP = { 1: "大包", 2: "小包", 3: "纯服务" };
  if (isBizJson(data)) {
    if (Number(data.code) !== 0) {
      return { code: Number(data.code), list: [], msg: data.msg };
    }
    var obj = data.obj || {};
    var clst = obj.clst || [];
    var dmap = obj.dmap || {};
    var instimap = obj.instimap || {};
    var pmap = obj.pmap || {};
    return {
      code: 0,
      list: clst.map(function (c, idx) {
        var insti = instimap[c.relatedInstitute] || {};
        var plan = pmap[c.id] || {};
        return {
          index: idx + 1,
          id: c.id,
          relatedInstitute: c.relatedInstitute,
          company: insti.name || "",
          startDate: dmap[c.tsStart] || "",
          endDate: dmap[c.tsEnd] || "",
          type: c.type,
          typeName: TYPE_MAP[c.type] || String(c.type || ""),
          fee: plan.authurizedFree != null ? plan.authurizedFree : "",
          currency: plan.currency || "",
          contractPaper: c.contractPaper || "",
          planId: plan.id || c.relatedInstitute,
          domestic: plan.domestic,
          foreignLimit: plan.foreignLimit,
        };
      }),
    };
  }
  if (typeof data === "string") {
    return {
      code: 0,
      list: parseHtmlTableRows(data).map(function (cells, idx) {
        var opCell = cells[8] || "";
        var serviceCell = cells[9] || "";
        return {
          index: Number(stripHtml(cells[0])) || idx + 1,
          company: stripHtml(cells[1]),
          startDate: stripHtml(cells[2]),
          endDate: stripHtml(cells[3]),
          typeName: stripHtml(cells[4]),
          fee: stripHtml(cells[5]),
          currency: stripHtml(cells[6]),
          contractPaper: extractHref(cells[7] || ""),
          id: extractFnArg(opCell, "updateItem") || extractFnArg(opCell, "del"),
          planId: extractFnArg(serviceCell, "openEdit"),
          relatedInstitute: "",
          type: "",
          domestic: "",
          foreignLimit: "",
        };
      }),
    };
  }
  return { code: -1, list: [], msg: "无法解析合同列表响应" };
}



// WEBPACK FOOTER //
// ./src/util/insureApiCompat.js